import {
  ButtonGroup,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';

import type { AssignmentFormData } from '../../types/assignmentTypes';
import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import SolidButton from '@/client/components/shared/buttons/SolidButton';
import { WorkflowType } from '@/client/services/api/graphql/gql/graphql';
import { useAdminAssignmentsStore } from '../../state/adminAssignmentsStore';
import { useConfigStore } from '@/client/services/state/configStore';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Audience500Modal() {
  const { t } = useTranslation();
  const { config } = useConfigStore();
  const location = useLocation();
  const { pathname } = location;

  const isInstance = pathname.includes('instance');
  const isCreate = pathname.includes('create');
  const isEdit = pathname.includes('edit');
  const isInstanceCreation = isInstance && isCreate;
  const isInstanceEdit = isInstance && isEdit;

  const { assignmentGroupSizeLimit } = config;

  const { watch } = useFormContext<AssignmentFormData>();
  const { settings } = watch();
  const { type } = settings;

  const {
    audience500ModalIsOpen,
    setAudience500ModalIsOpen,
    audienceExceedsLimitModalIsOpen,
    setAudienceExceedsLimitModalIsOpen,
    stepIndex,
    setStepIndex,
    audienceCount,
  } = useAdminAssignmentsStore();

  const onClose = () => {
    setAudience500ModalIsOpen(false);
    setAudienceExceedsLimitModalIsOpen(false);
  };

  const audienceLimitsMap = {
    exceeds500: {
      title: t('admin.assignments.audience.audience500Modal.title'),
      description: t('admin.assignments.audience.audience500Modal.description', {
        audienceCount: audienceCount.toLocaleString(),
      }),
      cancel: t('admin.assignments.audience.audience500Modal.cancel'),
      submit: t('admin.assignments.audience.audience500Modal.submit'),
      image: {
        src: '/public/images/All-Good-3--Streamline-Brooklyn.svg',
        alt: 'All good',
      },
      onSubmit: () => {
        setStepIndex(stepIndex + 1);
        onClose();
      },
    },
    exceedsMaxLimit: {
      title: t('admin.assignments.audience.audienceExceedsLimitModal.title'),
      description: t('admin.assignments.audience.audienceExceedsLimitModal.description', {
        assignmentGroupSizeLimit: assignmentGroupSizeLimit.toLocaleString(),
      }),
      cancel: '',
      submit: t('admin.assignments.audience.audienceExceedsLimitModal.submit'),
      image: {
        src: '/public/images/Logged-Out-4--Streamline-Brooklyn.svg',
        alt: 'Edit Audience',
      },
      onSubmit: () => {
        if (isInstanceCreation) {
          setStepIndex(0);

          onClose();

          return;
        }

        if (type === WorkflowType.PointInTime) {
          setStepIndex(1);

          onClose();

          return;
        }

        setStepIndex(isCreate ? 2 : 1);

        onClose();
      },
    },
  };

  const modalType = audienceExceedsLimitModalIsOpen ? 'exceedsMaxLimit' : 'exceeds500';

  return (
    <Modal
      isOpen={audience500ModalIsOpen || audienceExceedsLimitModalIsOpen}
      onClose={onClose}
      isCentered
    >
      <ModalOverlay />

      <ModalContent paddingTop={12} paddingX={10} paddingBottom={8}>
        <ModalBody>
          <VStack spacing={4} marginBottom={10} textAlign="center">
            <Image
              src={audienceLimitsMap[modalType].image.src}
              alt={audienceLimitsMap[modalType].image.alt}
            />

            <Text fontSize="20px" fontWeight={600}>
              {audienceLimitsMap[modalType].title}
            </Text>

            <Text fontSize="14px" fontWeight={500}>
              {audienceLimitsMap[modalType].description}
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup width="full" justifyContent="center">
            {modalType === 'exceeds500' && (
              <OutlineButton variant="createCancel" onClick={onClose}>
                {audienceLimitsMap[modalType].cancel}
              </OutlineButton>
            )}

            <SolidButton onClick={audienceLimitsMap[modalType].onSubmit}>
              {audienceLimitsMap[modalType].submit}
            </SolidButton>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
